import React, { FC } from 'react';
import { Container, fade, Grid, makeStyles, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { Separator, SpacingLayout } from 'components/atoms';
import { SilverResultsNew } from 'templates/services';

interface SilverSectionProps {
  header: string;
  subHeader: string;
  textContentList: Array<{ title: string; description: string }>;
  staticsList: Array<{ quantity: string; text: string }>;
}

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    [theme.breakpoints.down(375)]: {
      fontSize: 32,
    },
  },
  content: {
    width: '150%',
    height: 176,
    backgroundColor: theme.palette.text.secondary,
    boxShadow: `0px 4px 48px ${fade(theme.palette.common.black, 0.15)}`,
    color: theme.palette.primary.main,
    top: 25,
    position: 'absolute',
    padding: theme.spacing(4, 4),
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    left: '50%',
    [theme.breakpoints.up('md')]: {
      width: 390,
      height: 280,
      overflow: 'auto',
      alignItems: 'flex-start',
      paddingLeft: 26,
      right: -110,
      left: 'auto',
      padding: theme.spacing(4, 8),
    },
  },
  subHeader: {
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    marginTop: 9,
    width: '100%',
    paddingLeft: 30,
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
      paddingLeft: 60,
    },
    [theme.breakpoints.down(375)]: {
      fontSize: 17,
    },
  },
  separator: {
    position: 'absolute',
    top: '30%',
    margin: 0,
    left: 10,
    width: 30,
    [theme.breakpoints.up(375)]: {
      top: '39%',
    },
    [theme.breakpoints.up('md')]: {
      left: 0,
      width: '100%',
    },
  },
  h2: {
    color: theme.palette.primary.main,
  },
  text: {
    lineHeight: '32px',
    marginBottom: theme.spacing(12),
  },
  grid: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  postContent: {
    position: 'relative',
    background: theme.palette.primary.main,
    color: theme.palette.text.secondary,
    height: 240,
    maxWidth: '50%',
    [theme.breakpoints.up('md')]: {
      height: 380,
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap',
    },
  },
  contentResults: {
    maxWidth: 'initial',
    flexBasis: 'initial',
    marginTop: 40,
    position: 'relative',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    [theme.breakpoints.up('md')]: {
      flexWrap: 'wrap',
    },
    [theme.breakpoints.up('md')]: {
      margin: 0,
      width: '130%',
      left: '-25%',
      position: 'absolute',
      bottom: -25,
      right: 0,
    },
    [theme.breakpoints.up('lg')]: {
      width: '120%',
      left: '-10%',
    },
  },
  subTitle: {
    paddingLeft: 0,
    color: theme.palette.primary.main,
    position: 'relative',
    paddingRight: 32,
    marginTop: 50,
    [theme.breakpoints.up('md')]: {
      paddingLeft: 32,
      marginTop: 0,
    },
  },
  subDescription: {
    paddingRight: 32,
    paddingLeft: 0,
    marginTop: 10,
    [theme.breakpoints.up('md')]: {
      paddingLeft: 32,
      marginTop: 20,
    },
  },
}));

const SilverSection: FC<SilverSectionProps> = ({ header, subHeader, textContentList, staticsList }) => {
  const classes = useStyles();
  const isSmallUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  return (
    <SpacingLayout>
      <Container maxWidth={false} fixed className={classes.grid}>
        <Grid
          lg={4}
          md={4}
          sm={12}
          xs={12}
          item
          className={classes.postContent}
          data-aos="zoom-in"
          data-aos-anchor-placement="top-bottom"
        >
          <Grid className={classes.content} data-aos="zoom-in" data-aos-delay="200" data-aos-anchor-placement="bottom-bottom">
            <Typography variant="h1" component="p" className={classes.header}>
              <span>{header}</span>
            </Typography>
            <Typography variant="h2" className={classes.subHeader}>
              <Separator my={2} className={classes.separator} />
              {subHeader}
            </Typography>
          </Grid>
        </Grid>
        <Grid className={classes.grid} md={6} xs={12} item>
          {!!textContentList.length && (
            <Grid xs={12} item className={classes.row}>
              {textContentList.map((text, index) => (
                <Grid data-aos="fade-up" data-aos-delay={(index + 2) * 100} xs={12} lg={6} item key={index}>
                  <Typography variant="h2" component="h2" className={classes.subTitle}>
                    <span>{text.title}</span>
                    <Separator my={2} />
                  </Typography>
                  <Typography className={classes.subDescription}>{text.description}</Typography>
                </Grid>
              ))}
            </Grid>
          )}
          {!!staticsList.length && (
            <Grid container className={classes.contentResults} spacing={isSmallUp ? 0 : 5}>
              {staticsList.map((stat, index) => (
                <Grid data-aos="fade-up" data-aos-delay={(index + 2) * 100} xs={'auto'} sm={12} item key={index}>
                  <SilverResultsNew result={stat.quantity} description={stat.text} />
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Container>
    </SpacingLayout>
  );
};

export default SilverSection;
